import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { STREAM_URL } from "@/lib/use-radio-stream";

export interface RecordingSource {
  episodeId: string;
  iconUrl: string | null;
  kind: "recording";
  showName: string;
  title: string;
  url: string;
}

export type PlayerSource = RecordingSource | { kind: "live" };

interface PlayerState {
  current: PlayerSource | null;
  currentTime: number;
  duration: number;
  playing: boolean;
  volume: number;
  playLive: () => Promise<void>;
  playRecording: (source: Omit<RecordingSource, "kind">) => Promise<void>;
  seek: (fraction: number) => void;
  setVolume: (value: number) => void;
  toggle: () => Promise<void>;
}

const PlayerContext = createContext<PlayerState | null>(null);

export function usePlayer(): PlayerState {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
}

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [current, setCurrent] = useState<PlayerSource | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(75);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume / 100;
    }
  }, [volume]);

  const loadAndPlay = useCallback(async (src: string) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.getAttribute("src") !== src) {
      audio.setAttribute("src", src);
      audio.load();
    }

    setDuration(0);
    setCurrentTime(0);
    setPlaying(true);
    await audio.play();
  }, []);

  const playRecording = useCallback(
    async (source: Omit<RecordingSource, "kind">) => {
      setCurrent({ ...source, kind: "recording" });
      await loadAndPlay(source.url);
    },
    [loadAndPlay]
  );

  const playLive = useCallback(async () => {
    setCurrent({ kind: "live" });
    await loadAndPlay(STREAM_URL);
  }, [loadAndPlay]);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !current) {
      return;
    }

    if (audio.paused || audio.ended) {
      await audio.play();
    } else {
      audio.pause();
    }
  }, [current]);

  const seek = useCallback((fraction: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const time = fraction * audio.duration;
    if (Number.isFinite(time)) {
      audio.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const changeVolume = useCallback((value: number) => {
    setVolume(value);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = value / 100;
    }
  }, []);

  const value = useMemo<PlayerState>(
    () => ({
      current,
      currentTime,
      duration,
      playLive,
      playRecording,
      playing,
      seek,
      setVolume: changeVolume,
      toggle,
      volume,
    }),
    [
      changeVolume,
      current,
      currentTime,
      duration,
      playLive,
      playRecording,
      playing,
      seek,
      toggle,
      volume,
    ]
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef}>
        <track kind="captions" />
      </audio>
    </PlayerContext.Provider>
  );
}
